import React from "react";
import { Card, Col, Row, Typography } from "antd";
import { formatTraceNumber, dashIfEmpty } from "../utils/priceTraceFormatters";

const { Text } = Typography;

const BIG_DELTA_PCT = 2;

function StatCard({ label, value, warn }) {
  return (
    <Card size="small" className={`apt-summary-card ${warn ? "apt-summary-card--warn" : ""}`}>
      <div className="apt-summary-card__label">{label}</div>
      <div className="apt-summary-card__value">{dashIfEmpty(value)}</div>
    </Card>
  );
}

export default function AdminPriceTraceSummary({ summary, entityType }) {
  const s = summary || {};
  const deltaPct = s.deltaPct != null ? Number(s.deltaPct) : null;
  const warnDelta = deltaPct != null && Math.abs(deltaPct) >= BIG_DELTA_PCT;

  const apiRaw = formatTraceNumber(s.apiRaw);
  const normalized = formatTraceNumber(s.normalized);
  const finalDisplay =
    entityType === "hotel"
      ? dashIfEmpty(s.finalDisplay != null ? String(s.finalDisplay) : null)
      : formatTraceNumber(s.finalDisplay);
  const deltaAbs = formatTraceNumber(s.deltaAbs);
  const deltaPctStr = s.deltaPct != null ? `${Number(s.deltaPct).toFixed(2)}%` : null;
  const commission = formatTraceNumber(s.commissionTotal);
  const fx = s.exchangeRate != null ? formatTraceNumber(s.exchangeRate) : null;
  const rooms = s.rooms != null ? String(s.rooms) : null;
  const passengers = s.passengers != null ? String(s.passengers) : null;

  if (entityType === "organized_tour") {
    const beforeHr = formatTraceNumber(s.totalBeforeCommission);
    const finalIlsUi =
      s.finalDisplay != null ? `${formatTraceNumber(s.finalDisplay)} ₪` : null;

    const apiGrossCard = dashIfEmpty(s.orgTourApiGrossDisplay);
    const apiUnit = s.orgTourApiUnit ? ` (${s.orgTourApiUnit})` : "";
    const supCommPerPerson = dashIfEmpty(s.orgTourSupplierCommissionPerPersonDisplay);
    const adminDiscount = dashIfEmpty(s.orgTourAdminDiscountDisplay);
    const agentNet = dashIfEmpty(s.orgTourAgentNetDisplay ?? finalIlsUi);
    const supCommCard = dashIfEmpty(s.orgTourSupplierCommissionDisplay);
    const supNetCard = dashIfEmpty(s.orgTourSupplierNetDisplay ?? normalized);
    const fxCard = dashIfEmpty(s.orgTourFxDisplay ?? (fx != null ? String(fx) : null));
    const hrCard = dashIfEmpty(s.orgTourHrDisplay ?? (commission != null ? `${commission} ₪` : null));
    const finalChainCard = dashIfEmpty(s.orgTourFinalDisplay ?? finalIlsUi);

    return (
      <div className="apt-summary">
        <Row gutter={[8, 8]}>
          <Col xs={24} sm={24} lg={24}>
            <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
              TRACE ראשי: מחיר API ← עמלת ספק לאדם ← סה״כ עמלה ← הנחת אדמין ← לפני HR ← אחרי HR (נטו לסוכן)
            </Text>
          </Col>
          <Col xs={12} sm={8} lg={6}>
            <StatCard label={`מחיר API${apiUnit}`} value={apiGrossCard} />
          </Col>
          <Col xs={12} sm={8} lg={6}>
            <StatCard label="עמלת ספק לאדם" value={supCommPerPerson} />
          </Col>
          <Col xs={12} sm={8} lg={6}>
            <StatCard
              label="סה״כ עמלה"
              value={dashIfEmpty(s.orgTourSupplierCommissionTotalDisplay)}
            />
          </Col>
          <Col xs={12} sm={8} lg={6}>
            <StatCard label="הנחת אדמין" value={adminDiscount} />
          </Col>
          <Col xs={12} sm={8} lg={6}>
            <StatCard
              label="לפני עמלת HR"
              value={dashIfEmpty(s.orgTourBeforeHrDisplay ?? beforeHr)}
            />
          </Col>
          <Col xs={12} sm={8} lg={6}>
            <StatCard
              label="אחרי עמלת HR · נטו לסוכן סופי"
              value={dashIfEmpty(s.orgTourAfterHrDisplay ?? agentNet)}
            />
          </Col>
          {s.orgTourAgentNetDisplay &&
          s.orgTourAfterHrDisplay &&
          s.orgTourAgentNetDisplay !== s.orgTourAfterHrDisplay ? (
            <Col xs={12} sm={8} lg={6}>
              <StatCard label="נטו לסוכן סופי" value={agentNet} />
            </Col>
          ) : null}
          {s.orgTourAdminDiscountHint ? (
            <Col xs={24}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {s.orgTourAdminDiscountHint}
              </Text>
            </Col>
          ) : null}
          <Col xs={24} sm={24} lg={24}>
            <Text type="secondary" style={{ display: "block", margin: "8px 0 4px" }}>
              שרשרת מלאה: API Gross ← עמלת ספק ← נטו ספק ← FX ← HR ← סופי
            </Text>
          </Col>
          <Col xs={12} sm={8} lg={8}>
            <StatCard label="Supplier Commission (סוג · ערך · ירידה · אפקטיבי)" value={supCommCard} />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <StatCard label="Supplier Net (מקור)" value={supNetCard} />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <StatCard label="FX (שער)" value={fxCard} />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <StatCard label="HR (ש״ח)" value={hrCard} />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <StatCard label="Final (ש״ח)" value={finalChainCard} />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <StatCard label="בסיס לפני HR (ILS)" value={dashIfEmpty(beforeHr)} />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <StatCard label="מחיר מוצג UI (ש״ח)" value={dashIfEmpty(finalIlsUi)} />
          </Col>
          <Col xs={24} sm={24} lg={8}>
            <Card size="small" className="apt-summary-card">
              <div className="apt-summary-card__label">מטבע ספק → תצוגה</div>
              <div className="apt-summary-card__value">
                <Text type="secondary">
                  {dashIfEmpty(s.sourceCurrency)} → {dashIfEmpty(s.displayCurrency)}
                </Text>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  if (entityType === "package") {
    const flight = formatTraceNumber(s.flightPrice);
    const hotel = formatTraceNumber(s.hotelPrice);
    const extras = formatTraceNumber(s.extras);
    const beforeComm = formatTraceNumber(s.totalBeforeCommission);
    return (
      <div className="apt-summary">
        <Row gutter={[8, 8]}>
          <Col xs={12} sm={8} lg={4}>
            <StatCard label="מחיר טיסה (מקור)" value={dashIfEmpty(flight)} />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <StatCard label="מחיר מלון (מקור)" value={dashIfEmpty(hotel)} />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <StatCard label="אקסטרות / תוספות" value={dashIfEmpty(extras)} />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <StatCard label="סה״כ לפני עמלה (ILS)" value={dashIfEmpty(beforeComm)} />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <StatCard label="עמלה / markup" value={dashIfEmpty(commission)} />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <StatCard label="מחיר סופי (תצוגה)" value={dashIfEmpty(finalDisplay)} />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <StatCard label="מחיר בסיס חבילה (API)" value={apiRaw} />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <StatCard label="שער המרה" value={dashIfEmpty(fx)} />
          </Col>
          <Col xs={24} sm={16} lg={8}>
            <Card size="small" className="apt-summary-card">
              <div className="apt-summary-card__label">מטבע מקור / תצוגה</div>
              <div className="apt-summary-card__value">
                <Text type="secondary">
                  {dashIfEmpty(s.sourceCurrency)} → {dashIfEmpty(s.displayCurrency)}
                </Text>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="apt-summary">
      <Row gutter={[8, 8]}>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="תגובת API גולמית" value={apiRaw} />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="אחרי נירמול" value={normalized} />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="תצוגה סופית" value={finalDisplay} />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="הפרש מוחלט" value={deltaAbs} warn={warnDelta} />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="הפרש באחוזים" value={dashIfEmpty(deltaPctStr)} warn={warnDelta} />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="עמלה" value={commission} />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="שער חליפין" value={fx} />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="חדרים" value={dashIfEmpty(rooms)} />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="נוסעים" value={dashIfEmpty(passengers)} />
        </Col>
        <Col xs={24} sm={16} lg={8}>
          <Card size="small" className="apt-summary-card">
            <div className="apt-summary-card__label">מטבע מקור / תצוגה</div>
            <div className="apt-summary-card__value">
              <Text type="secondary">
                {dashIfEmpty(s.sourceCurrency)} → {dashIfEmpty(s.displayCurrency)}
              </Text>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
